import React, { useState, useEffect } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stepper,
  Step,
  StepLabel,
  LinearProgress
} from "@mui/material";
import {
  CloudUpload,
  Download,
  CheckCircle,
  ErrorOutline,
  Description,
  PersonAdd,
  TableChart
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const UploadSalary = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // ========== SINGLE EMPLOYEE STATE ==========
  const [employeeId, setEmployeeId] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [ctc, setCtc] = useState("");
  const [pfno, setPfno] = useState("");
  const [pfnoError, setPfnoError] = useState("");
  const [uan, setUan] = useState("");
  const [uanError, setUanError] = useState("");
  const [baseSalaryPercent, setBaseSalaryPercent] = useState("");
  const [hraPercent, setHraPercent] = useState("");
  const [pfPercent, setPfPercent] = useState("");
  const [gratuityAmount, setGratuityAmount] = useState("");
  const [performanceBonus, setPerformanceBonus] = useState("");
  const [professionalTax, setProfessionalTax] = useState("");
  const [medicalInsurance, setMedicalInsurance] = useState("");
  const [joiningBonus, setJoiningBonus] = useState("");
  const [variableSalary, setVariableSalary] = useState("");
  const [isJoiningBonusPaid, setIsJoiningBonusPaid] = useState(false);
  const [isVariableSalaryPaid, setIsVariableSalaryPaid] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);
  const [existingPayroll, setExistingPayroll] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(true);
  const [isConfigSaved, setIsConfigSaved] = useState(false);
  const [vpfAmount, setVpfAmount] = useState("0");
  const location = useLocation();

  // ========== BULK UPLOAD STATE ==========
  const [bulkStep, setBulkStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const bulkSteps = ['Upload File', 'Validate Data', 'Process & Confirm'];

  useEffect(() => {
    if (activeTab === 0 && !employeeDetails) {
      setOpenDialog(true);
    }
  }, [location.pathname, activeTab, employeeDetails]);

  // ========== VALIDATION FUNCTIONS ==========
  const validatePfno = (value) => {
    const pfRegex = /^[A-Z0-9/\-]+$/;
    if (value.trim()) {
      if (!pfRegex.test(value)) {
        setPfnoError("Invalid PF Number format");
        return false;
      } else {
        setPfnoError("");
        return true;
      }
    } else {
      setPfnoError("");
      return true;
    }
  };

  const validateUan = (value) => {
    const uanRegex = /^\d{12}$/;
    if (value.trim()) {
      if (!uanRegex.test(value)) {
        setUanError("UAN must be exactly 12 digits");
        return false;
      } else {
        setUanError("");
        return true;
      }
    } else {
      setUanError("");
      return true;
    }
  };

  const handlePfnoChange = (e) => {
    const value = e.target.value;
    setPfno(value);
    validatePfno(value);
  };

  const handleUanChange = (e) => {
    const value = e.target.value;
    setUan(value);
    validateUan(value);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // ========== SINGLE EMPLOYEE FUNCTIONS ==========
  const fetchEmployeeDetails = async () => {
    if (!employeeId.trim()) {
      showSnackbar("Please enter an Employee ID");
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    try {
      setLoading(true);
      setPreviewData(null);
      setIsValid(false);
      setErrorMessage("");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const empRes = await axios.get(`${API_URL}/api/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setEmployeeDetails(empRes.data.data);

      const configRes = await axios.get(`${API_URL}/api/salary/config/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (configRes.data.data) {
        const config = configRes.data.data;
        setExistingConfig(config);
        setBaseSalaryPercent(config.base_salary_percentage || "");
        setHraPercent(config.hra_percentage || "");
        setPfPercent(config.pf_percentage || "");
        setGratuityAmount(config.gratuity_amount || "");
        setVpfAmount(config.vpf_amount || "0");
        setPerformanceBonus(config.performance_bonus_amount || "");
        setProfessionalTax(config.professional_tax_amount || "");
        setMedicalInsurance(config.medical_insurance_amount || "");
        setIsConfigSaved(true);
        showSnackbar("Existing config loaded. Enter CTC to preview.");
      } else {
        setExistingConfig(null);
        setIsConfigSaved(false);
        setBaseSalaryPercent("50");
        setHraPercent("40");
        setPfPercent("12");
        setGratuityAmount("0");
        setVpfAmount("0");
        setPerformanceBonus("0");
        setProfessionalTax("2500");
        setMedicalInsurance("0");
      }

      try {
        const payrollRes = await axios.get(`${API_URL}/api/payroll/employee/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        if (payrollRes.data && payrollRes.data.payroll) {
          const payroll = payrollRes.data.payroll;
          setExistingPayroll(payroll);
          setPfno(payroll.pfno || "");
          setUan(payroll.uan || "");
          setCtc(payroll.ctc || "");
          setJoiningBonus(payroll.joining_bonus || "");
          setVariableSalary(payroll.variable_salary || "");
          setIsJoiningBonusPaid(payroll.joining_bonus_paid || false);
          setIsVariableSalaryPaid(payroll.variable_salary_paid || false);
        }
      } catch (payrollError) {
      }

      setOpenDialog(false);
    } catch (error) {
      //console.error("Error fetching details:", error);
      showSnackbar("Employee not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!ctc || parseFloat(ctc) <= 0) {
      showSnackbar("Please enter a valid CTC");
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        employee_id: employeeId,
        ctc: parseFloat(ctc)
      };

      const response = await axios.post(`${API_URL}/api/salary/config/preview`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (response.data.success) {
        setPreviewData(response.data.data);
        setIsValid(response.data.data.isValid);
        if (!response.data.data.isValid) {
          setErrorMessage(response.data.data.message);
        } else {
          setErrorMessage("");
        }
      }
    } catch (error) {
      //console.error("Error previewing salary:", error);
      showSnackbar(error.response?.data?.message || "Error calculating preview");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    if (!baseSalaryPercent || !hraPercent || !pfPercent) {
      showSnackbar("Base Salary %, HRA % and PF % are required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        employee_id: employeeId,
        base_salary_percentage: parseFloat(baseSalaryPercent),
        hra_percentage: parseFloat(hraPercent),
        pf_percentage: parseFloat(pfPercent),
        gratuity_amount: parseFloat(gratuityAmount) || 0,
        vpf_amount: parseFloat(vpfAmount) || 0,
        performance_bonus_amount: parseFloat(performanceBonus) || 0,
        professional_tax_amount: parseFloat(professionalTax) || 0,
        medical_insurance_amount: parseFloat(medicalInsurance) || 0,
        change_reason: existingConfig ? "Config updated by finance team" : "Initial config creation"
      };

      const response = await axios.post(`${API_URL}/api/salary/config/save`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (response.data.success) {
        setExistingConfig(response.data.data);
        setIsConfigSaved(true);
        showSnackbar(existingConfig ? "Config updated successfully!" : "Config created successfully!");
        if (ctc && parseFloat(ctc) > 0) {
          await handlePreview();
        }
      }
    } catch (error) {
      //console.error("Error saving config:", error);
      showSnackbar(error.response?.data?.message || "Error saving config");
    } finally {
      setLoading(false);
    }
  };

  const submitPayroll = async () => {
    let isPfnoValid = true;
    let isUanValid = true;

    if (pfno.trim()) {
      isPfnoValid = validatePfno(pfno);
    }
    if (uan.trim()) {
      isUanValid = validateUan(uan);
    }

    if (!isPfnoValid || !isUanValid || !ctc) {
      showSnackbar("Please fill in all required fields correctly. CTC is mandatory.");
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        employee_id: employeeId.trim(),
        pfno: pfno.trim(),
        uan: uan.trim(),
        ctc: parseFloat(ctc),
        joining_bonus: parseFloat(joiningBonus) || 0,
        variable_salary: parseFloat(variableSalary) || 0,
        is_joining_bonus_paid: isJoiningBonusPaid,
        is_variable_salary_paid: isVariableSalaryPaid
      };

      await axios.post(`${API_URL}/api/payroll/createPayroll`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json"
        },
        withCredentials: true
      });

      showSnackbar(`Salary details ${existingPayroll ? "updated" : "submitted"} successfully!`);
    } catch (error) {
      //console.error("Error submitting payroll:", error);
      showSnackbar(error.response?.data?.message || `Error ${existingPayroll ? "updating" : "submitting"} salary details`);
    } finally {
      setLoading(false);
    }
  };

  // ========== BULK UPLOAD FUNCTIONS ==========
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setBulkError(null);
      setValidationResult(null);
      setUploadResult(null);
      setBulkStep(0);
    }
  };

  const downloadTemplate = async () => {
    try {
      setDownloadLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/bulk-upload/download-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        withCredentials: true
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_bulk_template_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      //console.error('Error downloading template:', error);
      setBulkError('Failed to download template. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const validateFile = async () => {
    if (!selectedFile) {
      setBulkError('Please select a file first');
      return;
    }

    try {
      setBulkUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API_URL}/api/bulk-upload/validate`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setValidationResult(response.data.data);
      setBulkStep(1);
      setBulkError(null);
    } catch (error) {
      //console.error('Validation error:', error);
      setBulkError(error.response?.data?.message || 'File validation failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const processBulkUpload = async () => {
    try {
      setBulkUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API_URL}/api/bulk-upload/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setUploadResult(response.data.data);
      setBulkStep(2);
      setBulkError(null);
    } catch (error) {
      //console.error('Upload error:', error);
      setBulkError(error.response?.data?.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkStep(0);
    setSelectedFile(null);
    setValidationResult(null);
    setUploadResult(null);
    setBulkError(null);
  };

  // ========== RENDER BULK VALIDATION RESULTS ==========
  const renderBulkValidationResults = () => {
    if (!validationResult) return null;

    const { summary, validRows, invalidRows } = validationResult;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Validation Summary
          </Typography>
          
          <Box display="flex" gap={3} mb={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">Total Rows</Typography>
              <Typography variant="h5">{summary.totalRows}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="success.main">Valid Rows</Typography>
              <Typography variant="h5" color="success.main">{summary.valid}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="error.main">Invalid Rows</Typography>
              <Typography variant="h5" color="error.main">{summary.invalid}</Typography>
            </Box>
          </Box>

          {summary.invalid > 0 && (
            <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.errors.map((err, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{err.rowIndex}</TableCell>
                      <TableCell>{err.employee_id}</TableCell>
                      <TableCell>
                        {err.errors.map((e, i) => (
                          <Chip
                            key={i}
                            label={e}
                            size="small"
                            color="error"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={processBulkUpload}
              disabled={summary.valid === 0 || bulkUploading}
            >
              {bulkUploading ? <CircularProgress size={24} /> : 'Process Upload'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ========== RENDER BULK UPLOAD RESULTS ==========
  const renderBulkUploadResults = () => {
    if (!uploadResult) return null;

    const { success, failed } = uploadResult;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Results
          </Typography>

          <Box display="flex" gap={3} mb={3}>
            <Box>
              <Typography variant="body2" color="textSecondary">Processed</Typography>
              <Typography variant="h5">{uploadResult.total}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="success.main">Success</Typography>
              <Typography variant="h5" color="success.main">{success.length}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="error.main">Failed</Typography>
              <Typography variant="h5" color="error.main">{failed.length}</Typography>
            </Box>
          </Box>

          {success.length > 0 && (
            <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>CTC</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {success.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.employee_id}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>₹{item.ctc?.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label="Success" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {failed.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failed.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.employee_id || 'Unknown'}</TableCell>
                      <TableCell>{item.reason}</TableCell>
                      <TableCell>
                        <Chip label="Failed" color="error" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="outlined" onClick={resetBulkUpload}>
              Upload Another File
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ========== RENDER SINGLE EMPLOYEE FORM ==========
  const renderSingleEmployeeForm = () => (
    <>
      {/* Initial Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: '40vh',
            width: '600px',
            maxWidth: '90vw',
            margin: 'auto'
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}
      >
        <DialogTitle>
          {existingPayroll ? "Update Salary Details" : "Upload CTC Details"}
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Employee ID"
            variant="outlined"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            margin="normal"
            required
          />
          <Box display="flex" justifyContent="center" mt={3} gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchEmployeeDetails}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "FETCH"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setOpenDialog(false)}
            >
              CANCEL
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Main Container */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {!employeeDetails && !openDialog && (
          <Paper elevation={3} sx={{ p: 6, textAlign: "center", mt: 4 }}>
            <PersonAdd sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Employee Selected
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Please enter an Employee ID to view and configure salary details.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              startIcon={<PersonAdd />}
            >
              Fetch Employee Details
            </Button>
          </Paper>
        )}

        {employeeDetails && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight="bold">
                Salary Configuration - {employeeDetails.firstName} {employeeDetails.lastName}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setOpenDialog(true)}
                startIcon={<PersonAdd />}
              >
                Change Employee
              </Button>
            </Box>

            {existingConfig && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Existing config found. Update values and click "Update Config" to save changes.
              </Alert>
            )}

            {existingPayroll && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Existing payroll found. Any changes will update the current record.
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Employee Details */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography><strong>Employee ID:</strong> {employeeDetails.employee_id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography><strong>Name:</strong> {employeeDetails.firstName} {employeeDetails.lastName}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography><strong>City:</strong> {employeeDetails.city}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* CTC Input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CTC (Annual)"
                  variant="outlined"
                  type="number"
                  value={ctc}
                  onChange={(e) => setCtc(e.target.value)}
                  required
                  helperText="Enter Annual CTC to see salary breakdown"
                />
              </Grid>

              {/* PF and UAN */}
              <Grid item xs={12} md={3}>
                <Tooltip title="Enter the PF number of the employee" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="PF Number"
                    variant="outlined"
                    value={pfno}
                    onChange={handlePfnoChange}
                    error={!!pfnoError}
                    helperText={pfnoError}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Enter the UAN number of the employee" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="UAN"
                    variant="outlined"
                    value={uan}
                    onChange={handleUanChange}
                    error={!!uanError}
                    helperText={uanError}
                  />
                </Tooltip>
              </Grid>

              {/* Config Fields - Percentage based */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  <strong>Salary Components Configuration (Percentages)</strong>
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Base Salary as percentage of CTC" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="Base Salary %"
                    variant="outlined"
                    type="number"
                    value={baseSalaryPercent}
                    onChange={(e) => setBaseSalaryPercent(e.target.value)}
                    required
                    inputProps={{ step: "0.00000001" }}
                    helperText="e.g., 50.00"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="HRA as percentage of CTC" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="HRA %"
                    variant="outlined"
                    type="number"
                    value={hraPercent}
                    onChange={(e) => setHraPercent(e.target.value)}
                    required
                    inputProps={{ step: "0.00000001" }}
                    helperText="e.g., 40.00"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="PF as percentage of Base Salary" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="PF %"
                    variant="outlined"
                    type="number"
                    value={pfPercent}
                    onChange={(e) => setPfPercent(e.target.value)}
                    required
                    inputProps={{ step: "0.00000001" }}
                    helperText="e.g., 12.00"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Fixed annual gratuity amount" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="Gratuity Amount"
                    variant="outlined"
                    type="number"
                    value={gratuityAmount}
                    onChange={(e) => setGratuityAmount(e.target.value)}
                    inputProps={{ step: "0.01" }}
                    helperText="Enter fixed annual amount"
                  />
                </Tooltip>
              </Grid>

              {/* Config Fields - Fixed Amounts */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  <strong>Fixed Amount Components</strong>
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Fixed annual performance bonus amount" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="Performance Bonus"
                    variant="outlined"
                    type="number"
                    value={performanceBonus}
                    onChange={(e) => setPerformanceBonus(e.target.value)}
                    inputProps={{ step: "0.01" }}
                    helperText="Enter fixed annual amount"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Fixed annual professional tax amount" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="Professional Tax"
                    variant="outlined"
                    type="number"
                    value={professionalTax}
                    onChange={(e) => setProfessionalTax(e.target.value)}
                    inputProps={{ step: "0.01" }}
                    helperText="Enter fixed annual amount"
                    disabled
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="Fixed annual medical insurance amount" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="Medical Insurance"
                    variant="outlined"
                    type="number"
                    value={medicalInsurance}
                    onChange={(e) => setMedicalInsurance(e.target.value)}
                    inputProps={{ step: "0.01" }}
                    helperText="Enter fixed annual amount"
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={3}>
                <Tooltip title="VPF (Voluntary PF) - Fixed annual amount (Optional)" placement="top" arrow>
                  <TextField
                    fullWidth
                    label="VPF Amount (Optional)"
                    variant="outlined"
                    type="number"
                    value={vpfAmount}
                    onChange={(e) => setVpfAmount(e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }}
                    helperText="Enter fixed annual VPF amount"
                  />
                </Tooltip>
              </Grid>

              {/* Bonus Fields */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  <strong>Bonus & Variable Pay</strong>
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Joining Bonus"
                  variant="outlined"
                  type="number"
                  value={joiningBonus}
                  onChange={(e) => setJoiningBonus(e.target.value)}
                  inputProps={{ min: "0" }}
                  helperText="Enter joining bonus amount"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Variable Salary"
                  variant="outlined"
                  type="number"
                  value={variableSalary}
                  onChange={(e) => setVariableSalary(e.target.value)}
                  inputProps={{ min: "0" }}
                  helperText="Enter variable salary amount"
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="center" mt={3} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveConfig}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : existingConfig ? "Update Config" : "Save Config"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handlePreview}
                    disabled={!ctc || loading || !isConfigSaved}
                  >
                    Preview Salary
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={submitPayroll}
                    disabled={loading || !isValid || !previewData}
                  >
                    {loading ? <CircularProgress size={24} /> : existingPayroll ? "Update Payroll" : "Generate Payroll"}
                  </Button>
                </Box>
              </Grid>

              {/* Preview Section */}
              {previewData && (
                <Grid item xs={12}>
                  <Card sx={{ mt: 3, backgroundColor: previewData.isValid ? "#e8f5e9" : "#ffebee" }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Salary Breakdown Preview</Typography>

                      {!previewData.isValid && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {previewData.message}
                        </Alert>
                      )}

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>CTC:</strong> {formatCurrency(previewData.ctc)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Base Salary:</strong> {formatCurrency(previewData.baseSalary)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>HRA:</strong> {formatCurrency(previewData.hra)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Employee PF:</strong> {formatCurrency(previewData.employeePF)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Employer PF:</strong> {formatCurrency(previewData.employerPF)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Gratuity:</strong> {formatCurrency(previewData.gratuity)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Performance Bonus:</strong> {formatCurrency(previewData.performanceBonus)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Professional Tax:</strong> {formatCurrency(previewData.professionalTax)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>Medical Insurance:</strong> {formatCurrency(previewData.medicalInsurance)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography><strong>VPF:</strong> {formatCurrency(previewData.vpf)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography color="primary" fontWeight="bold">
                            <strong>Special Allowance:</strong> {formatCurrency(previewData.specialAllowance)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" color={previewData.isValid ? "success.main" : "error.main"}>
                            Status: {previewData.isValid ? "✅ Valid Configuration" : "❌ Invalid - Please adjust values"}
                          </Typography>
                          {!previewData.isValid && previewData.excessAmount && (
                            <Typography variant="body2" color="error">
                              Excess Amount: {formatCurrency(previewData.excessAmount)}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>

                      {previewData.isValid && (
                        <Box display="flex" justifyContent="center" mt={3}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={submitPayroll}
                            disabled={loading}
                            size="large"
                          >
                            {loading ? <CircularProgress size={24} /> : existingPayroll ? "Update Payroll" : "Generate Payroll"}
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
      </Container>
    </>
  );

  // ========== RENDER BULK UPLOAD FORM ==========
  const renderBulkUploadForm = () => (
   <Container maxWidth={false} sx={{ mt: 4, mb: 4, width: '100%' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
          Bulk Payroll Upload
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center" paragraph>
          Upload Excel file with multiple employee salary configurations
        </Typography>

        <Stepper activeStep={bulkStep} sx={{ my: 4 }}>
          {bulkSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {bulkError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setBulkError(null)}>
            {bulkError}
          </Alert>
        )}

        {/* Step 0: Upload */}
        {bulkStep === 0 && (
          <Box>
            <Box
              border="2px dashed #ccc"
              borderRadius={2}
              p={4}
              textAlign="center"
              bgcolor="#fafafa"
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="bulk-file-upload"
              />
              <label htmlFor="bulk-file-upload">
                <Button variant="contained" component="span" startIcon={<CloudUpload />}>
                  Select File
                </Button>
              </label>
              {selectedFile && (
                <Box mt={2}>
                  <Typography>
                    <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box display="flex" justifyContent="center" gap={2} mt={3}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadTemplate}
                disabled={downloadLoading}
              >
                {downloadLoading ? <CircularProgress size={24} /> : 'Download Template'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={validateFile}
                disabled={!selectedFile || bulkUploading}
              >
                {bulkUploading ? <CircularProgress size={24} /> : 'Validate File'}
              </Button>
            </Box>

            <Box mt={3}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Instructions:</strong>
                  <br />
                  1. Download the template Excel file
                  <br />
                  2. Fill in employee CTC, PF, UAN, and percentage fields
                  <br />
                  3. Upload the filled file for validation
                  <br />
                  4. Review validation results and process the upload
                  <br />
                  5. <strong>Note:</strong> Existing employee configs will be updated
                </Typography>
              </Alert>
            </Box>
          </Box>
        )}

        {/* Step 1: Validate */}
        {bulkStep === 1 && renderBulkValidationResults()}

        {/* Step 2: Results */}
        {bulkStep === 2 && renderBulkUploadResults()}

        {bulkUploading && (
          <Box mt={3}>
            <LinearProgress />
            <Typography variant="body2" color="textSecondary" align="center" mt={1}>
              Processing file...
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );

  // ========== MAIN RENDER ==========
  return (
    <>
      <Container maxWidth={false} sx={{ mt: 3, width: '100%' }}>
        <Paper sx={{ mb: 3 }}>
                    <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              if (newValue === 0 && !employeeDetails) {
                setOpenDialog(true);
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              icon={<PersonAdd />} 
              label="Single Employee" 
              iconPosition="start"
            />
            <Tab 
              icon={<TableChart />} 
              label="Bulk Upload" 
              iconPosition="start"
            />
          </Tabs>

        </Paper>

        {activeTab === 0 && renderSingleEmployeeForm()}
        {activeTab === 1 && renderBulkUploadForm()}
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default UploadSalary;